import type { CalendarEvent, IsoDate, Tag, TaskInstance, User } from '@hestia/core';
import { TaskRow } from '../components/TaskRow.js';
import { EventRow } from '../components/EventRow.js';
import { Icon, icons } from '../ui.js';
import { bucket, fullDate, greeting } from '../lang.js';

interface Props {
  date: IsoDate;
  me: User;
  /** Set when the list is filtered to one person. */
  viewing: User | null;
  stillOpen: TaskInstance[];
  today: TaskInstance[];
  /** Tasks running over several days that are on now, or start within a week. */
  spanning: TaskInstance[];
  events: CalendarEvent[];
  calendarHues: Map<string, string>;
  users: Map<string, User>;
  tags: Map<string, Tag>;
  onToggle: (i: TaskInstance) => void;
  onOpen: (i: TaskInstance) => void;
  onMoveToToday: (i: TaskInstance) => void;
  onAdd: () => void;
}

/** No counters anywhere on this screen — not in the header, not on the sections.
 *  The list is the information; a tally of it would only ever be a scoreboard. */
export function Today({ date, me, viewing, stillOpen, today, spanning, events, calendarHues, users, tags, onToggle, onOpen, onMoveToToday, onAdd }: Props) {
  const morning = today.filter((i) => bucket(i) === 'morning');
  const later = today.filter((i) => bucket(i) === 'later');
  // Ones already running read differently from ones still to come, and they are
  // the ones you can act on, so they go first and under their own heading.
  const running = spanning.filter((i) => i.date <= date);
  const coming = spanning.filter((i) => i.date > date);
  const empty = !stillOpen.length && !today.length && !spanning.length && !events.length;

  const rowProps = { users, tags, today: date, onToggle, onOpen };

  return (
    <div className="page">
      <header style={{ paddingTop: 6 }}>
        <div className="sec">{fullDate(date)}</div>
        <h1 className="dsp" style={{ fontSize: 34, lineHeight: 1.08, margin: '9px 0 0' }}>
          {viewing
            ? (viewing.id === me.id ? 'Your day' : `${viewing.displayName}'s day`)
            : `${greeting()}, ${me.displayName}`}
        </h1>
      </header>

      {running.length > 0 && (
        <section className="section">
          <div className="sec">On now</div>
          {running.map((i) => (
            <TaskRow key={`span:${i.task.id}`} instance={i} {...rowProps} />
          ))}
        </section>
      )}

      {stillOpen.length > 0 && (
        <section className="section">
          <div className="sec">Still open</div>
          {stillOpen.map((i) => (
            <TaskRow key={`${i.task.id}:${i.originalDate}`} instance={i}
                     {...rowProps} onMoveToToday={onMoveToToday} />
          ))}
        </section>
      )}

      {events.length > 0 && (
        <section className="section">
          {/* What is happening, as opposed to what needs doing. */}
          <div className="sec">On the calendar</div>
          {events.map((e) => (
            <EventRow key={e.id} event={e} hue={calendarHues.get(e.calendarId) ?? 'sky'} />
          ))}
        </section>
      )}

      {morning.length > 0 && (
        <section className="section">
          <div className="sec">Morning</div>
          {morning.map((i) => (
            <TaskRow key={`${i.task.id}:${i.originalDate}`} instance={i} {...rowProps} />
          ))}
        </section>
      )}

      {later.length > 0 && (
        <section className="section">
          <div className="sec">Afternoon &amp; evening</div>
          {later.map((i) => (
            <TaskRow key={`${i.task.id}:${i.originalDate}`} instance={i} {...rowProps} />
          ))}
        </section>
      )}

      {/* Not yet started, but close enough to be worth knowing about. Sits after
          the day itself, because it is not something to do now. */}
      {coming.length > 0 && (
        <section className="section">
          <div className="sec">Later this week</div>
          {coming.map((i) => (
            <TaskRow key={`span:${i.task.id}`} instance={i} {...rowProps} />
          ))}
        </section>
      )}

      {empty ? (
        <div className="empty" style={{ justifyContent: 'center', padding: '30px 19px' }}>
          {viewing ? `Nothing on ${viewing.id === me.id ? 'your' : `${viewing.displayName}'s`} list today.`
                   : 'Nothing on today.'}
        </div>
      ) : (
        <button className="empty" onClick={onAdd}>
          <Icon path={icons.plus} size={17} w={2.2} />Add a task for today
        </button>
      )}
    </div>
  );
}
